import { useEffect, useState } from 'react'
import { BottomSheet } from '@astryxdesign/core/BottomSheet'
import { Button } from '@astryxdesign/core/Button'
import { Text } from '@astryxdesign/core/Text'
import { Stack } from '@astryxdesign/core/Stack'
import { Grid } from '@astryxdesign/core/Grid'
import { ToggleButton } from '@astryxdesign/core/ToggleButton'
import { TextInput } from '@astryxdesign/core/TextInput'
import { Selector } from '@astryxdesign/core/Selector'
import { useCategories, useMe, useTrip, useUsers } from '#/lib/hooks'
import { enqueue } from '#/lib/sync'
import { formatMinor, parseAmount, splitEqually } from '#/lib/money'
import type { Category, SplitMode } from '#/lib/types'

/*
  The interaction the whole app lives or dies on. If adding a chai costs six
  taps you stop on day three and the ledger becomes fiction.

  purpose='info' allows Escape, a backdrop tap and swipe-to-dismiss, because a
  sheet you cannot flick away feels stuck. It was 'form', which blocks both, to
  protect a typed amount — the draft below protects it instead, so dismissing
  costs nothing.

  height='hug' fits the content; 'tall' left a screenful of dead space. Note
  that only a fully expanded 'tall' sheet lifts a focused field above the mobile
  keyboard, which is why the amount uses our own keypad rather than a field.

  The keypad stays ours — a custom pad avoids the focus delay and the wrong
  keyboard layout — but it is built from Grid and Buttons, not CSS.
*/

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', '⌫']

/**
 * Draft survives the sheet closing.
 *
 * purpose='info' is what makes swipe-to-dismiss work, and it enables
 * tap-the-backdrop too — so a half-typed expense can now be dismissed by
 * accident, which is exactly what purpose='form' was guarding against.
 * Keeping the draft outside the component makes that cheap instead of
 * costly: dismiss it however you like, reopen, and it is still there.
 * Cleared on save.
 */
let draft = { raw: '', category: 'food' as Category, paidBy: '', note: '' }

export function AddExpenseSheet({ onClose }: { onClose: () => void }) {
  const trip = useTrip()
  const users = useUsers()
  const me = useMe()
  const categories = useCategories(trip?.id)

  // Seeded from the draft, so a dismissed sheet reopens where you left it.
  const [raw, setRaw] = useState(draft.raw)
  const [category, setCategory] = useState<Category>(draft.category)
  const [paidBy, setPaidBy] = useState<string>(draft.paidBy)
  // Always split across the party. A personal spend is rare enough that it is
  // not worth a control that sits on the critical path of every entry.
  const splitMode: SplitMode = 'equal'
  const [note, setNote] = useState(draft.note)

  // Mirror every change into the draft, so whichever way the sheet is
  // dismissed — Escape, backdrop, swipe — the typing survives.
  useEffect(() => {
    draft = { raw, category, paidBy, note }
  }, [raw, category, paidBy, note])

  const active = users.filter((u) => u.status === 'active')
  const payer = paidBy || me?.id || active[0]?.id || ''
  const minor = parseAmount(raw)
  // A note is required: an amount with no note is a row you cannot explain a
  // week later, which is how a ledger stops being worth keeping.
  const canSave = minor > 0 && note.trim().length > 0

  function tap(key: string) {
    if (key === '⌫') return setRaw((r) => r.slice(0, -1))
    if (key === '.' && raw.includes('.')) return
    if (raw.includes('.') && raw.split('.')[1]?.length >= 2) return
    if (raw.replace('.', '').length > 8) return
    setRaw((r) => (r === '0' && key !== '.' ? key : r + key))
  }

  async function save() {
    if (!canSave || !trip || !payer) return
    const participants = splitMode === 'none' ? [payer] : active.map((u) => u.id)

    await enqueue({
      table: 'expenses',
      op: 'upsert',
      row: {
        id: crypto.randomUUID(),
        trip_id: trip.id,
        amount_minor: minor,
        currency: trip.base_currency,
        category,
        paid_by: payer,
        split_mode: splitMode,
        note: note.trim(),
        spent_at: Date.now(),
        created_by: payer,
      },
      shares: splitEqually(minor, participants),
    })

    // Saved, so the draft is spent.
    draft = { raw: '', category: 'food', paidBy: '', note: '' }
    // Closes immediately. The row is on screen before the request leaves.
    onClose()
  }

  return (
    <BottomSheet
      isOpen
      onOpenChange={(open) => { if (!open) onClose() }}
      // 'info' allows Escape, a backdrop tap and swipe-to-dismiss. The draft
      // above is what makes that safe.
      purpose="info"
      height="hug"
      label="Add expense"
    >
      {/* The sheet's chrome — grab handle and the scroll fade under it — sits at
          the top edge. Extra block-start padding keeps the Save button clear of
          the fade rather than half-veiled by it. */}
      <Stack direction="vertical" gap={3} padding={4} paddingBlockStart={6}>
        <Stack direction="horizontal" justify="between" align="center" gap={2}>
          <Button label="Cancel" variant="ghost" size="lg" onClick={onClose} />
          <Text type="supporting">{trip?.name ?? 'No trip'}</Text>
          <Button
            label="Save"
            variant="primary"
            size="lg"
           
            isDisabled={!canSave}
            clickAction={save}
          />
        </Stack>

        <Stack direction="vertical" align="center" gap={0}>
          <Text type="display-2" hasTabularNumbers display="block">
            {minor > 0 ? formatMinor(minor, trip?.base_currency) : '₹0'}
          </Text>
        </Stack>

        {/* A grid, not a scrolling chip row. Chips were 28px tall and 4px
            apart — the smallest and most crowded targets in the app, on the one
            flow used twenty times a day. Nothing scrolls out of reach.

            Icon first, label small underneath: the icon is what you aim at
            after the second day, and shrinking the word is what makes room for
            four across and for however many the party adds. The touch target
            is held at 44px by app.css, so size='sm' shrinks the type without
            shrinking what your thumb has to hit. */}
        <Grid columns={3} gap={2} className="cat-grid">
          {categories.map((c) => (
            <ToggleButton
              key={c.id}
              size="sm"
              label={c.label}
              icon={<Text type="inherit">{c.icon}</Text>}
              isPressed={category === c.id}
              onPressedChange={() => setCategory(c.id)}
            >
              <Text size="xsm" maxLines={1}>{c.label}</Text>
            </ToggleButton>
          ))}
        </Grid>

        <TextInput
          label="What was it for?"
          value={note}
          onChange={(v) => setNote(v)}
          placeholder="Chai and samosa"
          isRequired
        />

        {/* Nearly always you. A four-across grid of names spent a quarter of
            the sheet restating that, so it is a quiet selector that already
            says your name and only needs touching on the rare occasion
            somebody else paid. */}
        <Selector
          label="Paid by"
          isLabelHidden
          variant="ghost"
          size="sm"
          presentation="adaptive"
          value={payer}
          onChange={(v) => setPaidBy(v)}
          options={active.map((u) => ({
            value: u.id,
            label: u.id === me?.id ? `Paid by me` : `Paid by ${u.display_name}`,
          }))}
        />

        <Grid columns={3} gap={2}>
          {KEYS.map((k) => (
            <Button
              key={k}
              label={k}
              variant="secondary"
              size="lg"
              width="100%"
              onClick={() => tap(k)}
            />
          ))}
        </Grid>
      </Stack>
    </BottomSheet>
  )
}
